import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { normaliserTexte } from '@/lib/normaliser';

interface MedicamentAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onMedicamentAdded?: (nom: string) => void;
}

export default function MedicamentAutocomplete({
  value,
  onChange,
  placeholder = 'Nom du médicament...',
  className = '',
  onMedicamentAdded,
}: MedicamentAutocompleteProps) {
  const [allMedicaments, setAllMedicaments] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastCommittedRef = useRef<string>(value);
  const isSelectingRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadAllMedicaments();
  }, []);

  useEffect(() => {
    lastCommittedRef.current = value;
  }, [value]);

  const loadAllMedicaments = async () => {
    try {
      const { data } = await supabase
        .from('medicaments')
        .select('nom')
        .order('nom', { ascending: true })
        .limit(3000);

      if (data && data.length > 0) {
        setAllMedicaments(data.map((m: any) => m.nom));
      }
    } catch (err) {
      console.error('Erreur chargement médicaments:', err);
    } finally {
      setLoaded(true);
    }
  };

  const filterSuggestions = (query: string) => {
    const q = normaliserTexte(query.trim());
    if (!q) {
      setSuggestions([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    const matches = allMedicaments
      .filter((nom) => normaliserTexte(nom).includes(q))
      .sort((a, b) => {
        const na = normaliserTexte(a);
        const nb = normaliserTexte(b);
        const aStarts = na.startsWith(q) ? 0 : 1;
        const bStarts = nb.startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return na.localeCompare(nb);
      })
      .slice(0, 8);

    setSuggestions(matches);
    setIsOpen(true);
    setHighlightedIndex(matches.length > 0 ? 0 : -1);
  };

  const autoAdd = async (nom: string) => {
    try {
      const { error } = await supabase.from('medicaments').insert({ nom });
      // 23505 = doublon, on l'ignore
      if (error && error.code !== '23505') {
        console.error('Erreur ajout médicament:', error);
      }
      setAllMedicaments((prev) =>
        prev.some((n) => normaliserTexte(n) === normaliserTexte(nom))
          ? prev
          : [...prev, nom].sort((a, b) => a.localeCompare(b, 'fr'))
      );
      if (onMedicamentAdded) onMedicamentAdded(nom);
    } catch (err) {
      console.error('Erreur ajout médicament:', err);
    }
  };

  const commitValue = (nom: string) => {
    const trimmed = nom.trim();
    if (!trimmed) return;
    onChange(trimmed);
    lastCommittedRef.current = trimmed;
    setIsOpen(false);
    setSuggestions([]);
    setHighlightedIndex(-1);

    // Ajout automatique en base si le médicament n'existe pas encore
    if (!allMedicaments.some((n) => normaliserTexte(n) === normaliserTexte(trimmed))) {
      autoAdd(trimmed);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    filterSuggestions(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          commitValue(suggestions[highlightedIndex]);
        } else {
          commitValue(value);
        }
        break;

      case 'Escape':
        setIsOpen(false);
        setSuggestions([]);
        break;

      default:
        break;
    }
  };

  const handleBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      if (isSelectingRef.current) {
        isSelectingRef.current = false;
        return;
      }
      const current = inputRef.current?.value ?? value;
      if (current.trim() && current.trim() !== lastCommittedRef.current) {
        commitValue(current);
      }
      setIsOpen(false);
    }, 150);
  };

  // Faire défiler la liste pour garder l'élément en surbrillance visible
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Fermer au clic extérieur (en dehors du conteneur complet, suggestions incluses)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasExactMatch = value.trim() !== '' &&
    allMedicaments.some((n) => normaliserTexte(n) === normaliserTexte(value.trim()));

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (value.trim().length >= 1) filterSuggestions(value);
          }}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
        />
        {!loaded && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
          </div>
        )}
      </div>

      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto"
        >
          {suggestions.length > 0 ? (
            suggestions.map((nom, index) => (
              <li
                key={nom}
                onPointerDown={() => { isSelectingRef.current = true; }}
                onClick={() => {
                  commitValue(nom);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-4 py-2.5 cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                  index === highlightedIndex
                    ? 'bg-teal-50 text-teal-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-capsule-line text-teal-500 flex-shrink-0"></i>
                <span>{nom}</span>
              </li>
            ))
          ) : (
            <li className="px-4 py-2.5 text-sm text-gray-400 flex items-center gap-2">
              <i className="ri-add-circle-line text-gray-300 flex-shrink-0"></i>
              {value.trim()
                ? 'Aucun résultat — sera ajouté automatiquement'
                : 'Tapez pour rechercher'}
            </li>
          )}
        </ul>
      )}

      {!isOpen && hasExactMatch && loaded && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <i className="ri-check-line text-green-500 text-sm"></i>
        </div>
      )}
    </div>
  );
}